// Components/ExportLeads.tsx
import React, { useState, useEffect, useRef } from "react";
import { Toaster, toast } from "react-hot-toast";
import {
  fetchMeadiaSourceData,
  fetchAllSubMediaSource,
  fetchUserGroupData,
  fetchUserData,
  GetAllFollowUpTypes,
  fetchProjectData,
  GetAllCampaignService,
} from "../Services/APIService";

// ─── Interfaces ────────────────────────────────────────────────────────────────

export interface LeadReportFilter {
  dateFrom?: string;
  dateTo?: string;
  followupTypeIds: number[];
  campaignIds: number[];
  projectIds: number[];
  sourceIds: number[];
  subSourceIds: number[];
  teamIds: number[];
  userIds: number[];
}

interface ExportLeadsProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (filters: LeadReportFilter) => void;
}

interface MediaSource { sourceId: number; sourceName: string; isActive: boolean; }
interface SubMediaSource { mediaSourceID: number; name: string; id: number; isActive: boolean; }
interface UserGroup { groupId: number; groupName: string; }
interface FollowupType { id: number; name: string; isActive: boolean; }
interface Project { projectID: number; projectName: string; isActive: boolean; }
interface CampaignItem { campaignId: number; campaignName: string; isActive: boolean; }
interface User { userId: number; userName: string; userTypeId: number; isActive: boolean; }

interface CampaignRow {
  id: number;
  campaignIds: number[];
  selectedCampaignIds: number[];
  projectIds: number[];
  sourceIds: number[];
  subSourceIds: number[];
}

type DurationPreset =
  | "Today" | "Yesterday" | "Last 7 Days" | "Last 30 Days"
  | "This Month" | "Last Month" | "Last 6 Months" | "Custom Range";

interface DateRange { start: Date | null; end: Date | null; }

// ─── Calendar helpers ──────────────────────────────────────────────────────────

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAY_HEADERS = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];

function daysInMonth(y: number, m: number) { return new Date(y, m + 1, 0).getDate(); }
function firstDayOf(y: number, m: number) { return new Date(y, m, 1).getDay(); }
function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function fmtDate(d: Date) { return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`; }
function toISODay(d: Date, end = false) {
  const copy = new Date(d);

  if (end) {
    copy.setHours(23, 59, 59, 999);
  } else {
    copy.setHours(0, 0, 0, 0);
  }

  const timezoneOffset = copy.getTimezoneOffset() * 60000;

  return new Date(copy.getTime() - timezoneOffset).toISOString();
}

// ─── CalendarMonth ────────────────────────────────────────────────────────────

function CalendarMonth({ year, month, range, hovered, onDayClick, onDayHover, onNavPrev, onNavNext }: {
  year: number; month: number; range: DateRange; hovered: Date | null;
  onDayClick: (d: Date) => void; onDayHover: (d: Date | null) => void;
  onNavPrev?: () => void; onNavNext?: () => void;
}) {
  const cells: (Date | null)[] = [];
  for (let i = 0; i < firstDayOf(year, month); i++) cells.push(null);
  for (let d = 1; d <= daysInMonth(year, month); d++) cells.push(new Date(year, month, d));

  function classify(date: Date): "start" | "end" | "in-range" | "none" {
    const { start, end } = range;
    if (start && sameDay(date, start)) return "start";
    if (end && sameDay(date, end)) return "end";
    const eff = end ?? hovered;
    if (start && eff) {
      const lo = start <= eff ? start : eff;
      const hi = start <= eff ? eff : start;
      if (date > lo && date < hi) return "in-range";
    }
    return "none";
  }

  return (
    <div style={{ width: "100%", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
        <button onClick={onNavPrev} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "#777", width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 4 }}>‹</button>
        <span style={{ flex: 1, textAlign: "center", fontSize: 13, fontWeight: 600, color: "#333" }}>{MONTHS_SHORT[month]} {year}</span>
        <button onClick={onNavNext} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: "#777", width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 4 }}>›</button>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", marginBottom: 2 }}>
        {DAY_HEADERS.map(d => <div key={d} style={{ textAlign: "center", fontSize: 10, color: "#aaa", padding: "2px 0" }}>{d}</div>)}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: "1px 0" }}>
        {cells.map((date, i) => {
          if (!date) return <div key={i} />;
          const cls = classify(date);
          const ep = cls === "start" || cls === "end";
          return (
            <div key={i} onClick={() => onDayClick(date)} onMouseEnter={() => onDayHover(date)} onMouseLeave={() => onDayHover(null)}
              style={{
                textAlign: "center", fontSize: 12, padding: "5px 2px", cursor: "pointer", borderRadius: 4, userSelect: "none",
                background: ep ? "#7c3aed" : cls === "in-range" ? "#ede4f7" : "transparent",
                color: ep ? "#fff" : "#333", fontWeight: ep ? 700 : 400
              }}>
              {date.getDate()}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── DateRangePicker ──────────────────────────────────────────────────────────

function DateRangePicker({ value, onChange, onClose }: {
  value: DateRange; onChange: (r: DateRange) => void; onClose: () => void;
}) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const [preset, setPreset] = useState<DurationPreset>("Last 7 Days");
  const [local, setLocal] = useState<DateRange>(value);
  const [hovered, setHovered] = useState<Date | null>(null);
 const [leftDate, setLeftDate] = useState(
  new Date(today.getFullYear(), today.getMonth(), 1)
);

const [rightDate, setRightDate] = useState(
  new Date(today.getFullYear(), today.getMonth() + 1, 1)
);


const navLeft = (dir: -1 | 1) => {

  setLeftDate(prev =>
    new Date(
      prev.getFullYear(),
      prev.getMonth() + dir,
      1
    )
  );

};


const navRight = (dir: -1 | 1) => {

  setRightDate(prev =>
    new Date(
      prev.getFullYear(),
      prev.getMonth() + dir,
      1
    )
  );

};
  function handleDay(date: Date) {
    if (!local.start || (local.start && local.end)) { setLocal({ start: date, end: null }); setPreset("Custom Range"); }
    else { const s = local.start; setLocal(date < s ? { start: date, end: s } : { start: s, end: date }); }
  }

  function applyPreset(p: DurationPreset) {
    setPreset(p);
    const t = new Date(); t.setHours(0, 0, 0, 0);
    const map: Record<DurationPreset, () => DateRange> = {
      "Today": () => ({ start: t, end: t }),
      "Yesterday": () => { const y = new Date(t); y.setDate(y.getDate() - 1); return { start: y, end: y }; },
      "Last 7 Days": () => { const s = new Date(t); s.setDate(s.getDate() - 6); return { start: s, end: t }; },
      "Last 30 Days": () => { const s = new Date(t); s.setDate(s.getDate() - 29); return { start: s, end: t }; },
      "This Month": () => ({ start: new Date(t.getFullYear(), t.getMonth(), 1), end: t }),
      "Last Month": () => { const s = new Date(t.getFullYear(), t.getMonth() - 1, 1); const e = new Date(t.getFullYear(), t.getMonth(), 0); return { start: s, end: e }; },
      "Last 6 Months": () => { const s = new Date(t); s.setMonth(s.getMonth() - 6); return { start: s, end: t }; },
      "Custom Range": () => local,
    };
    if (p !== "Custom Range") setLocal(map[p]());
  }

  const presets: DurationPreset[] = ["Today", "Yesterday", "Last 7 Days", "Last 30 Days", "This Month", "Last Month", "Last 6 Months", "Custom Range"];

  return (
    <div style={{ position: "absolute", zIndex: 1500, top: "calc(100% + 4px)", left: 0, right: 0, background: "#fff", borderRadius: 8, boxShadow: "0 8px 32px rgba(0,0,0,0.15)", border: "1px solid #e0e0ee", overflow: "hidden" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "10px 12px", background: "#f7f5fb", borderBottom: "1px solid #ede8f5" }}>
        {presets.map(p => (
          <div key={p} onClick={() => applyPreset(p)} style={{ padding: "5px 12px", fontSize: 12, cursor: "pointer", borderRadius: 20, fontWeight: preset === p ? 700 : 400, color: preset === p ? "#fff" : "#555", background: preset === p ? "#7c3aed" : "#fff", border: preset === p ? "1px solid #7c3aed" : "1px solid #d8d8e8", transition: "all 0.15s", whiteSpace: "nowrap" }}>{p}</div>
        ))}
      </div>
      <div style={{ padding: "14px 12px", display: "flex", flexDirection: "column", gap: 10 }}>
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}>
            <CalendarMonth
 year={leftDate.getFullYear()}
 month={leftDate.getMonth()} range={local} hovered={hovered} onDayClick={handleDay} onDayHover={setHovered} onNavPrev={() => navLeft(-1)} onNavNext={() => navLeft(1)} />
          </div>
          <div style={{ width: 1, background: "#f0f0f8", flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <CalendarMonth
 year={rightDate.getFullYear()}
 month={rightDate.getMonth()} range={local} hovered={hovered} onDayClick={handleDay} onDayHover={setHovered} onNavPrev={() => navRight(-1)} onNavNext={() => navRight(1)} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", paddingTop: 8, borderTop: "1px solid #eee" }}>
          <button onClick={onClose} style={{ background: "#fff", color: "#555", border: "1px solid #ddd", borderRadius: 5, padding: "7px 16px", fontSize: 12, cursor: "pointer" }}>Cancel</button>
          <button onClick={() => { onChange(local); onClose(); }} style={{ background: "#7c3aed", color: "#fff", border: "none", borderRadius: 5, padding: "7px 20px", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>Apply</button>
        </div>
      </div>
    </div>
  );
}

// ─── ID-based MultiSelect ─────────────────────────────────────────────────────

interface IdOption { id: number; label: string; }

const MultiSelectById = ({ options, value, onChange, placeholder = "Select...", disabled = false }: {
  options: IdOption[];
  value: number[];
  onChange: (v: number[]) => void;
  placeholder?: string;
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = (id: number) =>
    onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);

  const remove = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== id));
  };

  const labelOf = (id: number) => options.find(o => o.id === id)?.label ?? String(id);
  const filteredOptions = options.filter(opt => opt.label.toLowerCase().includes(search.toLowerCase()));

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div onClick={() => !disabled && setOpen(v => !v)}
        style={{ minHeight: 36, padding: "4px 28px 4px 10px", border: open ? "1px solid #7c3aed" : "1px solid #d8d8e8", borderRadius: 6, fontSize: 13, background: disabled ? "#f5f5f8" : "#fff", cursor: disabled ? "not-allowed" : "pointer", display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center", position: "relative" }}>
        {value.length === 0 && <span style={{ color: "#b0b0c0" }}>{placeholder}</span>}
        {value.map(id => (
          <span key={id} style={{ background: "#ede4f7", padding: "2px 6px", borderRadius: 8 }}>
            {labelOf(id)}
            {!disabled && <span onClick={e => remove(id, e)}> ×</span>}
          </span>
        ))}
      </div>
      {open && !disabled && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #ddd", zIndex: 1000, maxHeight: 250, overflow: "auto", borderRadius: "0 0 6px 6px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
          <input type="text" placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ width: "100%", padding: "6px 10px", border: "none", borderBottom: "1px solid #eee", outline: "none", boxSizing: "border-box" }} />
          {filteredOptions.length === 0 ? (
            <div style={{ padding: 10, color: "#999" }}>No results</div>
          ) : (
            filteredOptions.map(opt => (
              <div key={opt.id} onClick={() => toggle(opt.id)}
                style={{ padding: "9px 10px", cursor: "pointer", background: value.includes(opt.id) ? "#f5f0fc" : "#fff", display: "flex", alignItems: "center", gap: 8, fontSize: 13, borderBottom: "1px solid #f5f5f5" }}>
                <input type="checkbox" checked={value.includes(opt.id)} readOnly style={{ accentColor: "#7c3aed" }} /> {opt.label}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ─── User MultiSelect — styled like screenshot ────────────────────────────────

const UserMultiSelect = ({ options, value, onChange }: {
  options: IdOption[];
  value: number[];
  onChange: (v: number[]) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const toggle = (id: number) =>
    onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);

  const remove = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(value.filter(v => v !== id));
  };

  const labelOf = (id: number) => options.find(o => o.id === id)?.label ?? String(id);
  const filtered = options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()));

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Trigger — matches screenshot border style */}
      <div onClick={() => setOpen(v => !v)}
        style={{
          minHeight: 40, padding: "6px 36px 6px 12px", border: open ? "1px solid #7c3aed" : "1px solid #c8c8d8",
          borderRadius: 6, fontSize: 13, background: "#fff", cursor: "pointer",
          display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center", position: "relative",
          boxShadow: open ? "0 0 0 2px #ede4f7" : "none", transition: "all 0.15s"
        }}>
        {value.length === 0 && <span style={{ color: "#b0b0c0", fontSize: 13 }}>Select User</span>}
        {value.map(id => (
          <span key={id} style={{ background: "#ede4f7", color: "#5b21b6", padding: "2px 8px", borderRadius: 10, fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
            {labelOf(id)}
            <span onClick={e => remove(id, e)} style={{ cursor: "pointer", fontSize: 14, lineHeight: 1, color: "#7c3aed" }}>×</span>
          </span>
        ))}
        {/* Chevron */}
        <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#888", fontSize: 13, pointerEvents: "none" }}>▾</span>
      </div>

      {/* Dropdown — exactly like screenshot */}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 2px)", left: 0, right: 0, zIndex: 2000,
          background: "#fff", border: "1px solid #c8c8d8", borderRadius: 6,
          boxShadow: "0 6px 24px rgba(0,0,0,0.13)", overflow: "hidden"
        }}>
          {/* Search input */}
          <div style={{ padding: "8px 10px", borderBottom: "1px solid #eee" }}>
            <input
              type="text"
              placeholder="Search user..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
              style={{
                width: "100%", padding: "6px 10px", border: "1px solid #d8d8e8",
                borderRadius: 5, fontSize: 13, outline: "none", boxSizing: "border-box",
                color: "#333"
              }}
            />
          </div>
          {/* Options list */}
          <div style={{ maxHeight: 220, overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <div style={{ padding: "10px 14px", color: "#999", fontSize: 13 }}>No users found</div>
            ) : (
              filtered.map((opt, idx) => {
                const selected = value.includes(opt.id);
                return (
                  <div key={opt.id} onClick={() => toggle(opt.id)}
                    style={{
                      padding: "10px 14px", cursor: "pointer", fontSize: 13,
                      background: selected ? "#ede4f7" : idx === 0 && !search ? "#f0f0f8" : "#fff",
                      color: selected ? "#5b21b6" : "#222",
                      fontWeight: selected ? 600 : 400,
                      display: "flex", alignItems: "center", gap: 10,
                      borderBottom: "1px solid #f5f5f5",
                      transition: "background 0.1s"
                    }}
                    onMouseEnter={e => { if (!selected) (e.currentTarget as HTMLDivElement).style.background = "#f7f5fb"; }}
                    onMouseLeave={e => { if (!selected) (e.currentTarget as HTMLDivElement).style.background = idx === 0 && !search ? "#f0f0f8" : "#fff"; }}
                  >
                    {/* Avatar circle */}
                    <div style={{
                      width: 28, height: 28, borderRadius: "50%",
                      background: selected ? "#7c3aed" : "#e0d8f0",
                      color: selected ? "#fff" : "#7c3aed",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 12, fontWeight: 700, flexShrink: 0
                    }}>
                      {opt.label.charAt(0).toUpperCase()}
                    </div>
                    {opt.label}
                    {selected && <span style={{ marginLeft: "auto", color: "#7c3aed", fontSize: 14 }}>✓</span>}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Active-filter pill summary ───────────────────────────────────────────────

function ActiveFilterBadge({ count }: { count: number }) {
  if (count === 0) return null;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", background: "#7c3aed", color: "#fff", fontSize: 10, fontWeight: 700, borderRadius: "50%", width: 18, height: 18, marginLeft: 6 }}>
      {count}
    </span>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

const ExportLeads: React.FC<ExportLeadsProps> = ({ isOpen, onClose, onSubmit }) => {

  const [mediaSources, setMediaSources] = useState<MediaSource[]>([]);
  const [allSubSources, setAllSubSources] = useState<SubMediaSource[]>([]);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [followupTypes, setFollowupTypes] = useState<FollowupType[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [campaignItems, setCampaignItems] = useState<IdOption[]>([]);
  const [users, setUsers] = useState<User[]>([]);

  const [dateRange, setDateRange] = useState<DateRange>({ start: null, end: null });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const dateRef = useRef<HTMLDivElement>(null);

  const [campaigns, setCampaigns] = useState<CampaignRow[]>([
    { id: 1, campaignIds: [], selectedCampaignIds: [], projectIds: [], sourceIds: [], subSourceIds: [] },
  ]);
  const [exportTeams, setExportTeams] = useState<number[]>([]);
  const [exportUsers, setExportUsers] = useState<number[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const [srcRes, subRes, groupRes, followupRes, projectRes, userRes, campRes] = await Promise.all([
          fetchMeadiaSourceData(),
          fetchAllSubMediaSource(),
          fetchUserGroupData(),
          GetAllFollowUpTypes(),
          fetchProjectData(),
          fetchUserData(),
          GetAllCampaignService(),
        ]);
        setMediaSources(srcRes.filter((s: MediaSource) => s.isActive));
        setAllSubSources(subRes.filter((s: SubMediaSource) => s.isActive));
        setUserGroups(groupRes);
        setFollowupTypes(
          followupRes.filter(
            (t: FollowupType) =>
              t.isActive && t.id !== 0 &&
              t.name.toLowerCase() !== "no followup" &&
              t.name.toLowerCase() !== "note"
          )
        );
        setProjects(projectRes.filter((p: Project) => p.isActive === true));
        const campList: CampaignItem[] = campRes?.campaigns ?? campRes ?? [];
        console.log("[ExportLeads] raw campaign API response:", campRes);
        console.log("[ExportLeads] campList sample:", campList.slice(0, 2));
        const mappedCampaigns = campList
          .filter((c: CampaignItem) => c.isActive !== false)
          .map((c: CampaignItem) => {
            const numId = Number(c.campaignId);
            return { id: isNaN(numId) ? c.campaignId as any : numId, label: c.campaignName };
          });
        console.log("[ExportLeads] mappedCampaigns sample:", mappedCampaigns.slice(0, 2));
        setCampaignItems(mappedCampaigns);
        setUsers(userRes.filter((u: User) => u.isActive));
      } catch {
        toast.error("Failed to load filter options");
      }
    })();
  }, [isOpen]);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (dateRef.current && !dateRef.current.contains(e.target as Node)) setShowDatePicker(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleClearAll = () => {
    setDateRange({ start: null, end: null });
    setCampaigns([{ id: 1, campaignIds: [], selectedCampaignIds: [], projectIds: [], sourceIds: [], subSourceIds: [] }]);
    setExportTeams([]);
    setExportUsers([]);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const allFollowupIds = Array.from(new Set(campaigns.flatMap(c => c.campaignIds)));
    const allCampaignIds = Array.from(new Set(campaigns.flatMap(c => c.selectedCampaignIds)));
    console.log("[ExportLeads] selectedCampaignIds per row:", campaigns.map(c => c.selectedCampaignIds));
    console.log("[ExportLeads] allCampaignIds going to filter:", allCampaignIds);
    const allProjectIds = Array.from(new Set(campaigns.flatMap(c => c.projectIds)));
    const allSourceIds = Array.from(new Set(campaigns.flatMap(c => c.sourceIds)));
    const allSubSourceIds = Array.from(new Set(campaigns.flatMap(c => c.subSourceIds)));

    const filters: LeadReportFilter = {
      dateFrom: dateRange.start
        ? toISODay(dateRange.start, false)
        : undefined,

      dateTo: dateRange.end
        ? toISODay(dateRange.end, true)
        : dateRange.start
          ? toISODay(dateRange.start, true)
          : undefined,

      followupTypeIds: allFollowupIds,
      campaignIds: allCampaignIds,
      projectIds: allProjectIds,
      sourceIds: allSourceIds,
      subSourceIds: allSubSourceIds,
      teamIds: exportTeams,
      userIds: exportUsers,
    };
    onSubmit(filters);
    onClose();
  };

  const updateIdField = (rowId: number, key: "campaignIds" | "projectIds", val: number[]) =>
    setCampaigns(p => p.map(c => c.id === rowId ? { ...c, [key]: val } : c));

  const handleSourceChange = (rowId: number, newIds: number[]) => {
    setCampaigns(p => p.map(c => {
      if (c.id !== rowId) return c;
      const validSub = allSubSources.filter(s => newIds.includes(s.mediaSourceID)).map(s => s.id);
      return { ...c, sourceIds: newIds, subSourceIds: c.subSourceIds.filter(id => validSub.includes(id)) };
    }));
  };

  const updateCampaignIds = (rowId: number, ids: number[]) =>
    setCampaigns(p => p.map(c => c.id === rowId ? { ...c, selectedCampaignIds: ids } : c));

  const handleSubSourceChange = (rowId: number, newIds: number[]) =>
    setCampaigns(p => p.map(c => c.id === rowId ? { ...c, subSourceIds: newIds } : c));

  const subOptsForRow = (row: CampaignRow): IdOption[] =>
    row.sourceIds.length === 0
      ? []
      : allSubSources.filter(s => row.sourceIds.includes(s.mediaSourceID)).map(s => ({ id: s.id, label: s.name }));

  const sourceOptions: IdOption[] = mediaSources.map(s => ({ id: s.sourceId, label: s.sourceName }));
  const teamOptions: IdOption[] = userGroups.map(g => ({ id: g.groupId, label: g.groupName }));
  const campaignOptions: IdOption[] = followupTypes.map(t => ({ id: t.id, label: t.name }));
  const projectOptions: IdOption[] = projects.map(p => ({ id: p.projectID, label: p.projectName }));

  const userTypeId = Number(localStorage.getItem("userTypeId"));
  const loggedUserId = Number(localStorage.getItem("UserId"));
  const isLoginAsManager = localStorage.getItem("isLoginAsManager") === "true";

  let filteredUserList = users;

  // Admin → show all users
  if (userTypeId === 1) {
    filteredUserList = users;
  }

  // Manager
  else if (userTypeId === 2) {
    if (isLoginAsManager) {
      // show manager + team
      filteredUserList = users.filter(
        (u: any) => u.userId === loggedUserId || u.reportTo === loggedUserId
      );
    } else {
      // manager behaving like normal user
      filteredUserList = users.filter((u: any) => u.userId === loggedUserId);
    }
  }

  // Normal User
  else {
    filteredUserList = users.filter((u: any) => u.userId === loggedUserId);
  }

  // Convert to dropdown options
  const userOptions: IdOption[] = filteredUserList.map((u: any) => ({
    id: u.userId,
    label: u.userName,
  }));

  const activeCount =
    (dateRange.start ? 1 : 0) +
    campaigns.flatMap(c => [...c.campaignIds, ...c.selectedCampaignIds, ...c.projectIds, ...c.sourceIds, ...c.subSourceIds]).length +
    exportTeams.length +
    exportUsers.length;

  const lbl: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, color: "#9090a8", textTransform: "uppercase",
    letterSpacing: 0.6, marginBottom: 6, display: "block",
  };

  const durationLabel = dateRange.start && dateRange.end
    ? `${fmtDate(dateRange.start)} – ${fmtDate(dateRange.end)}`
    : dateRange.start ? fmtDate(dateRange.start) : "";

  if (!isOpen) return null;

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000 }} />

      <div style={{ position: "fixed", top: 0, right: 0, bottom: 0, width: 480, zIndex: 1100, background: "#fff", display: "flex", flexDirection: "column", boxShadow: "-4px 0 32px rgba(0,0,0,0.18)", fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif", animation: "slideInFromRight 0.28s cubic-bezier(0.25,0.46,0.45,0.94)" }}>

        {/* Header */}
        <div style={{ background: "linear-gradient(135deg, rgb(109,87,94) 0%, rgb(109,87,94) 100%)", padding: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0, minHeight: 64 }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <h5 style={{ margin: 0, color: "#fff", fontSize: 17, fontWeight: 700, letterSpacing: 0.3 }}>Filter Leads</h5>
            <ActiveFilterBadge count={activeCount} />
          </div>
          <button onClick={onClose}
            style={{ background: "rgba(255,255,255,0.18)", border: "none", cursor: "pointer", width: 30, height: 30, borderRadius: 6, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 20, lineHeight: 1, padding: 0 }}
            onMouseEnter={e => (e.currentTarget.style.background = "rgba(255,255,255,0.28)")}
            onMouseLeave={e => (e.currentTarget.style.background = "rgba(255,255,255,0.18)")}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px 100px" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

            {/* Duration */}
            <div ref={dateRef} style={{ position: "relative" }}>
              <label style={lbl}>DURATION</label>
              <div onClick={() => setShowDatePicker(v => !v)}
                style={{ minHeight: 38, padding: "9px 12px", border: showDatePicker ? "1px solid #7c3aed" : "1px solid #d8d8e8", borderRadius: 6, fontSize: 13, color: durationLabel ? "#333" : "#b0b0c0", background: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", boxSizing: "border-box" }}>
                <span>{durationLabel || "Select date range..."}</span>
                <span style={{ fontSize: 10, color: "#b0b0c0" }}>▾</span>
              </div>
              {showDatePicker && (
                <DateRangePicker value={dateRange} onChange={setDateRange} onClose={() => setShowDatePicker(false)} />
              )}
            </div>

            {/* User */}
            <div>
              <label style={lbl}>USER</label>
              <UserMultiSelect options={userOptions} value={exportUsers} onChange={setExportUsers} />
            </div>

            {/* Apply By */}
            <div style={{ border: "1px solid #e8e8f4", borderRadius: 8, padding: 16, background: "#fafafe" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <label style={{ ...lbl, marginBottom: 0 }}>APPLY BY</label>
              </div>
              {campaigns.map((c, idx) => {
                const subOpts = subOptsForRow(c);
                const hasSrc = c.sourceIds.length > 0;
                return (
                  <div key={c.id} style={{ background: "#fff", border: "1px solid #e8e8f4", borderRadius: 6, padding: 14, marginTop: idx > 0 ? 10 : 0, position: "relative" }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 14px" }}>
                      <div>
                        <label style={lbl}>Follow-up Type</label>
                        <MultiSelectById options={campaignOptions} value={c.campaignIds} onChange={v => updateIdField(c.id, "campaignIds", v)} placeholder="Follow-up Type" />
                      </div>
                      <div>
                        <label style={lbl}>CAMPAIGN</label>
                        <MultiSelectById
                          options={campaignItems}
                          value={c.selectedCampaignIds}
                          onChange={v => updateCampaignIds(c.id, v)}
                          placeholder="Campaign"
                        />
                      </div>
                      <div>
                        <label style={lbl}>PROJECT</label>
                        <MultiSelectById options={projectOptions} value={c.projectIds} onChange={v => updateIdField(c.id, "projectIds", v)} placeholder="Project" />
                      </div>
                      <div>
                        <label style={lbl}>SOURCES</label>
                        <MultiSelectById options={sourceOptions} value={c.sourceIds} onChange={v => handleSourceChange(c.id, v)} placeholder="Source" />
                      </div>
                      <div style={{ gridColumn: "1 / -1" }}>
                        <label style={lbl}>SUB SOURCES</label>
                        <MultiSelectById options={subOpts} value={c.subSourceIds} onChange={v => handleSubSourceChange(c.id, v)} placeholder={hasSrc ? "Sub source" : "Select source first"} disabled={!hasSrc} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Team */}
            <div>
              <label style={lbl}>SELECT TEAM</label>
              <MultiSelectById options={teamOptions} value={exportTeams} onChange={setExportTeams} placeholder="Team" />
            </div>

          </div>
        </div>

        {/* Footer */}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: "14px 24px", background: "#fff", borderTop: "1px solid #eeeef8", display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 10, boxShadow: "0 -2px 12px rgba(0,0,0,0.06)" }}>
          <button onClick={handleClearAll}
            style={{ background: "linear-gradient(135deg, rgb(215,191,255) 0%, #020202 100%)", color: "#fff", border: "none", borderRadius: 6, padding: "10px 32px", fontSize: 13, cursor: "pointer", fontWeight: 700, boxShadow: "0 2px 8px rgba(124,58,237,0.35)" }}
            onMouseEnter={e => (e.currentTarget.style.opacity = "0.88")}
            onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
          >Clear all filters</button>
          <button onClick={handleSubmit}
            style={{ background: "linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)", color: "#fff", border: "none", borderRadius: 6, padding: "10px 32px", fontSize: 13, cursor: "pointer", fontWeight: 700, boxShadow: "0 2px 8px rgba(124,58,237,0.35)" }}
            onMouseEnter={e => (e.currentTarget.style.opacity = "0.88")}
            onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
          >Apply Filters {activeCount > 0 && `(${activeCount})`}</button>
        </div>
      </div>

      <style>{`
        @keyframes slideInFromRight {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);    opacity: 1; }
        }
      `}</style>
    </>
  );
};

export default ExportLeads;